import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCirclePlus,
  faCircleMinus,
} from "@fortawesome/free-solid-svg-icons";
import { documentService } from "../../../service";
import { toast } from "react-toastify";
import "./NewResponse.css";

const NewResponseOffcanvas = ({ show, onClose, form }) => {
  const [responseData, setResponseData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Auto-generate ID fields on open if marked as auto_generated
    if (form && form.fields) {
      const autoValues = {};
      form.fields.forEach((f) => {
        if (f.field_type === "id" && f.conditions?.auto_generated) {
          autoValues[f.label] = `ID-${Math.random()
            .toString(36)
            .substring(2, 8)
            .toUpperCase()}`;
        }
      });
      setResponseData(autoValues);
    }
  }, [form]);

  if (!show || !form) return null;

  /**  Handle input change */
  const handleChange = (label, value, fieldType) => {
    let parsedValue = value;

    if (fieldType === "integer")
      parsedValue = value ? parseInt(value, 10) : null;
    else if (fieldType === "float")
      parsedValue = value ? parseFloat(value) : null;
    else if (fieldType === "boolean") parsedValue = value === "true";
    else if (fieldType === "checkbox" && !Array.isArray(value))
      parsedValue = [];

    setResponseData((prev) => ({ ...prev, [label]: parsedValue }));
  };

  /**  Toggle checkbox option */
  const toggleCheckboxOption = (label, option) => {
    setResponseData((prev) => {
      const prevOptions = prev[label] || [];
      return prevOptions.includes(option)
        ? { ...prev, [label]: prevOptions.filter((opt) => opt !== option) }
        : { ...prev, [label]: [...prevOptions, option] };
    });
  };

  /**  Validate all fields */
  const validateForm = () => {
    const newErrors = {};

    form.fields?.forEach((field) => {
      const value = responseData[field.label];
      const cond = field.conditions || {};

      // Skip section-title
      if (field.field_type === "section-title") return;

      // Skip auto-generated ID (no input)
      if (field.field_type === "id" && cond.auto_generated) return;

      // Required check
      if (
        field.required &&
        (value === undefined ||
          value === null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0))
      ) {
        newErrors[field.label] = "Ce champ est obligatoire.";
        return;
      }

      // Type-specific validation
      if (value !== undefined && value !== null && value !== "") {
        if (
          (field.field_type === "integer" || field.field_type === "float") &&
          (cond.min || cond.max)
        ) {
          if (cond.min !== null && value < cond.min)
            newErrors[field.label] = `Valeur minimale : ${cond.min}`;
          if (cond.max !== null && value > cond.max)
            newErrors[field.label] = `Valeur maximale : ${cond.max}`;
        }

        if (field.field_type === "date" && (cond.min || cond.max)) {
          const valDate = new Date(value);
          if (cond.min && valDate < new Date(cond.min))
            newErrors[field.label] = `Date minimale : ${cond.min}`;
          if (cond.max && valDate > new Date(cond.max))
            newErrors[field.label] = `Date maximale : ${cond.max}`;
        }

        if (field.field_type === "radio" && cond.options?.length) {
          if (!cond.options.includes(value))
            newErrors[field.label] = "Option invalide.";
        }

        if (field.field_type === "checkbox" && cond.options?.length) {
          const invalid = (value || []).some(
            (opt) => !cond.options.includes(opt)
          );
          if (invalid)
            newErrors[field.label] = "Une ou plusieurs options sont invalides.";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /** Submit form */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Veuillez corriger les erreurs avant de soumettre.");
      return;
    }

    try {
      const payload = {
        values_json: responseData,
        form_id: form.id,
      };
      await documentService.create(payload);
      toast.success("Réponse enregistrée avec succès !");
      onClose();
    } catch (error) {
      console.error("Erreur lors de la création du document :", error);
      toast.error("Erreur lors de la création du document.");
    }
  };

  return (
    <div
      className="offcanvas-backdrop-custom"
      style={{ display: show ? "block" : "none" }}
    >
      <div
        className={`offcanvas offcanvas-end ${show ? "show" : ""}`}
        tabIndex="-1"
        style={{ visibility: show ? "visible" : "hidden" }}
      >
        {/* Header */}
        <div className="border-bottom d-flex justify-content-start align-items-center p-2">
          <button
            type="button"
            className="btn-close me-2"
            onClick={onClose}
          ></button>
          <h5 className="NewEntryoffcanvas-title">
            Répondre au formulaire :{form.title}
          </h5>
        </div>

        {/* Body */}
        <div className="offcanvas-body">
          <div className="doc-attr py-1 px-4 d-flex flex-column justify-content-start align-items-start mb-3">
            <h6
              className="w-100 py-2 my-2"
              style={{
                color: "rgba(77, 100, 102, 1)",
                borderRadius: "6px",
              }}
            >
              Description du formulaire
            </h6>
            <span className="doc-label d-block">{form.description}</span> 
          </div>
          <form onSubmit={handleSubmit}>
            {form.fields?.map((field) => (
              <div
                key={field.id}
                className="doc-attr py-1 px-4 d-flex flex-column justify-content-start align-items-start mb-3"
              >
                {/* Section Title */}
                {field.field_type === "section-title" ? (
                  <h6
                    className="w-100 text-center py-2 my-2"
                    style={{
                      backgroundColor: "rgba(77, 100, 102, 0.1)",
                      color: "rgba(77, 100, 102, 1)",
                      borderRadius: "6px",
                    }}
                  >
                    {field.label}
                  </h6>
                ) : (
                  <>
                    {/* Label */}
                    <span className="doc-label d-block">
                      {field.label}
                      {field.required && (
                        <span style={{ color: "red" }}> *</span>
                      )}
                    </span>

                    {/* --- Field Inputs --- */}

                    {/* Auto-generated ID (hidden) */}
                    {field.field_type === "id" &&
                      field.conditions?.auto_generated && (
                        <input
                          type="text"
                          className="doc-input"
                          value={responseData[field.label] || ""}
                          disabled
                        />
                      )}

                    {/* Manual ID */}
                    {field.field_type === "id" &&
                      !field.conditions?.auto_generated && (
                        <input
                          type="text"
                          className="doc-input"
                          placeholder="Saisir un identifiant..."
                          value={responseData[field.label] || ""}
                          onChange={(e) =>
                            handleChange(
                              field.label,
                              e.target.value,
                              field.field_type
                            )
                          }
                        />
                      )}

                    {/* String */}
                    {field.field_type === "string" && (
                      <input
                        type="text"
                        className="doc-input"
                        placeholder="Saisir du texte..."
                        onChange={(e) =>
                          handleChange(
                            field.label,
                            e.target.value,
                            field.field_type
                          )
                        }
                      />
                    )}

                    {/* Textarea */}
                    {field.field_type === "text" && (
                      <textarea
                        className="doc-input"
                        rows="3"
                        placeholder="Saisir un texte..."
                        onChange={(e) =>
                          handleChange(
                            field.label,
                            e.target.value,
                            field.field_type
                          )
                        }
                      ></textarea>
                    )}

                    {/* Integer */}
                    {field.field_type === "integer" && (
                      <input
                        type="number"
                        className="doc-input"
                        placeholder="Saisir un nombre entier..."
                        onChange={(e) =>
                          handleChange(
                            field.label,
                            e.target.value,
                            field.field_type
                          )
                        }
                      />
                    )}

                    {/* Float */}
                    {field.field_type === "float" && (
                      <input
                        type="number"
                        step="0.01"
                        className="doc-input"
                        placeholder="Saisir un nombre décimal..."
                        onChange={(e) =>
                          handleChange(
                            field.label,
                            e.target.value,
                            field.field_type
                          )
                        }
                      />
                    )}

                    {/* Boolean */}
                    {field.field_type === "boolean" && (
                      <div className="d-flex gap-3 mt-1">
                        {["true", "false"].map((opt) => (
                          <label
                            key={opt}
                            className="d-flex align-items-center gap-1"
                          >
                            <input
                              type="radio"
                              name={field.label}
                              value={opt}
                              onChange={(e) =>
                                handleChange(
                                  field.label,
                                  e.target.value,
                                  field.field_type
                                )
                              }
                            />
                            {opt === "true" ? "Oui" : "Non"}
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Date */}
                    {field.field_type === "date" && (
                      <input
                        type="date"
                        className="doc-input"
                        onChange={(e) =>
                          handleChange(
                            field.label,
                            e.target.value,
                            field.field_type
                          )
                        }
                      />
                    )}

                    {/* Checkbox */}
                    {field.field_type === "checkbox" && (
                      <div className="choices-list w-100">
                        {(field.conditions?.options || []).map(
                          (option, idx) => {
                            const selected =
                              responseData[field.label]?.includes(option);
                            return (
                              <div
                                key={idx}
                                className={`choice-item d-flex justify-content-between align-items-center ${
                                  selected ? "selected" : ""
                                }`}
                                onClick={() =>
                                  toggleCheckboxOption(field.label, option)
                                }
                              >
                                {option}
                                <FontAwesomeIcon
                                  className="select-icon"
                                  icon={selected ? faCircleMinus : faCirclePlus}
                                />
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}

                    {/* Radio */}
                    {field.field_type === "radio" && (
                      <div className="choices-list w-100">
                        {(field.conditions?.options || []).map(
                          (option, idx) => (
                            <div
                              key={idx}
                              className={`choice-item d-flex justify-content-between align-items-center ${
                                responseData[field.label] === option
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() =>
                                handleChange(
                                  field.label,
                                  option,
                                  field.field_type
                                )
                              }
                            >
                              {option}
                              <FontAwesomeIcon
                                className="select-icon"
                                icon={
                                  responseData[field.label] === option
                                    ? faCircleMinus
                                    : faCirclePlus
                                }
                              />
                            </div>
                          )
                        )}
                      </div>
                    )}

                    {/* Error message */}
                    {errors[field.label] && (
                      <span className="text-danger small mt-1">
                        {errors[field.label]}
                      </span>
                    )}
                  </>
                )}
              </div>
            ))}

            {/* Footer */}
            <div className="offcanvas-footer p-3 border-top d-flex justify-content-center bg-white">
              <button type="submit" className="btn-add-doc">
                <FontAwesomeIcon icon={faCheck} className="me-2" />
                Valider & Ajouter
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewResponseOffcanvas;
